import { APP_VERSION } from '../../version';
import { parseMarkdown, type ParsedDoc } from './markdown';

// Static user-guide content lives in public/guide/ (deliberately NOT public/user-guide/ — a public dir with the same name as the /user-guide route makes the dev server 301 a full page load to /user-guide/) and is fetched at
// runtime. Module-level cache (CLAUDE.md "Module-level cache pattern"): the
// content is public, static and identical for every user, so — unlike
// per-tenant lookups — nothing is cleared on logout.

export interface GuideChapterMeta {
  id: string;
  number: number;
  title: string;
  file: string;
  covers: string[];
}

export interface GuideManifest {
  title: string;
  asOf: { date: string; appVersion: string };
  chapters: GuideChapterMeta[];
}

export type ChapterContent =
  | { status: 'ok'; doc: ParsedDoc }
  | { status: 'missing' };

let manifestCache: GuideManifest | null = null;
let manifestInflight: Promise<GuideManifest> | null = null;
const chapterCache = new Map<string, ChapterContent>();
const chapterInflight = new Map<string, Promise<ChapterContent>>();

function url(path: string): string {
  // Cache-buster tied to the app version so a new release refetches the guide.
  return `${process.env.PUBLIC_URL ?? ''}/guide/${path}?v=${encodeURIComponent(APP_VERSION)}`;
}

async function fetchText(path: string): Promise<string | null> {
  const res = await fetch(url(path));
  if (!res.ok) return null;
  const text = await res.text();
  // The CRA dev server / SPA hosts answer unknown paths with index.html (200).
  if (/^\s*<(!doctype|html)/i.test(text)) return null;
  return text;
}

function validateManifest(raw: unknown): GuideManifest {
  const m = raw as Partial<GuideManifest> | null;
  if (!m || !Array.isArray(m.chapters) || !m.asOf) throw new Error('Invalid user guide manifest');
  return m as GuideManifest;
}

export function loadManifest(): Promise<GuideManifest> {
  if (manifestCache) return Promise.resolve(manifestCache);
  if (manifestInflight) return manifestInflight;
  manifestInflight = fetchText('manifest.json')
    .then((text) => {
      if (text === null) throw new Error('User guide manifest not found');
      const m = validateManifest(JSON.parse(text));
      manifestCache = m;
      manifestInflight = null;
      return m;
    })
    .catch((e: unknown) => {
      manifestInflight = null;
      throw e;
    });
  return manifestInflight;
}

export function getCachedManifest(): GuideManifest | null {
  return manifestCache;
}

export function getCachedChapter(id: string): ChapterContent | undefined {
  return chapterCache.get(id);
}

/** Never rejects: a missing/failed chapter resolves to { status: 'missing' } (not cached, so a later retry refetches). */
export function loadChapter(chapter: GuideChapterMeta): Promise<ChapterContent> {
  const hit = chapterCache.get(chapter.id);
  if (hit) return Promise.resolve(hit);
  const inflight = chapterInflight.get(chapter.id);
  if (inflight) return inflight;
  const p = fetchText(chapter.file)
    .then((text): ChapterContent => {
      if (text === null || !text.trim()) return { status: 'missing' };
      const content: ChapterContent = { status: 'ok', doc: parseMarkdown(text) };
      chapterCache.set(chapter.id, content);
      return content;
    })
    .catch((): ChapterContent => ({ status: 'missing' }))
    .then((c) => {
      chapterInflight.delete(chapter.id);
      return c;
    });
  chapterInflight.set(chapter.id, p);
  return p;
}

export function loadAllChapters(manifest: GuideManifest): Promise<Map<string, ChapterContent>> {
  return Promise.all(manifest.chapters.map((c) => loadChapter(c).then((r) => [c.id, r] as const))).then(
    (entries) => new Map(entries),
  );
}

/** Test helper. */
export function clearGuideCache(): void {
  manifestCache = null;
  manifestInflight = null;
  chapterCache.clear();
  chapterInflight.clear();
}
